import {inject, Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, ResolveFn, UrlTree} from '@angular/router';
import {map, Observable, shareReplay, zip} from 'rxjs';
import {InsurerService} from '../../shared/services/insurer/insurer.service';
import {TpaService} from '../../shared/services/tpa/tpa.service';
import {IndustrySegmentService} from '../../shared/services/industry-segment/industry-segment.service';
import {PolicyTypeService} from '../../shared/services/policy-type/policy-type.service';
import {SpinnerOverlayService} from '../../shared/services/spinner-overlay/spinner-overlay.service';
import {PolicyService} from '../compass/services/policy.service';
import {ClaimIntimationService} from './services/claim-intimation.service';
import {ClaimIntimation} from './services/claim-intimation';
import {InsurerApiAvailabilityService} from './services/insurer-api-availability.service';
import {InsurerApiAvailability} from './services/insurer-api-availability';

@Injectable({
  providedIn: 'root',
})
export class ClaimsGuard {
  private insurerService = inject(InsurerService);
  private policyTypeService = inject(PolicyTypeService);
  private tpaService = inject(TpaService);
  private industrySegmentService = inject(IndustrySegmentService);
  private spinner = inject(SpinnerOverlayService);

  canActivate(): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.spinner.performWithSpinner(zip(
      this.insurerService.loadInsurers(false),
      this.policyTypeService.loadPolicyDepartmentsAndTypes(false),
      this.tpaService.loadTpa(false),
      this.industrySegmentService.loadIndustrySegment(false)
    ).pipe(map(() => {
      return true;
    })));
  }
}

export const isPolicyTypeGPAGTLResolver: ResolveFn<boolean> = (
  route: ActivatedRouteSnapshot
) => {
  const policyTypeService = inject(PolicyService);

  return policyTypeService.isPolicyTypeGPAGTL(Number(route.params['policyId']));
};

export const isPolicyTypeLiabilityResolver: ResolveFn<boolean> = (
  route: ActivatedRouteSnapshot
) => {
  const policyTypeService = inject(PolicyService);

  return policyTypeService.isPolicyTypeLiability(Number(route.params['policyId']));
};

export const policyDetailsResolver: ResolveFn<any> = (
  route: ActivatedRouteSnapshot
) => {
  const policyService = inject(PolicyService);

  return policyService.getPolicyDetailsByPolicyId(Number(route.params['policyId']));
};

export const claimDetailsResolver: ResolveFn<ClaimIntimation> = (
  route: ActivatedRouteSnapshot
) => {
  const claimIntimationService = inject(ClaimIntimationService);

  return claimIntimationService.getClaimDetailsByClaimId(Number(route.params['claimId']), true);
};

export const insurerApiAvailabilityResolver: ResolveFn<InsurerApiAvailability[]> = () => {
  const insurerApiAvailabilityService = inject(InsurerApiAvailabilityService);

  return insurerApiAvailabilityService.getInsurerApiAvailability().pipe(
    map((availability) => {
      return availability;
    }),
    shareReplay({
      bufferSize: 1,
      refCount: true,
    })
  );
};
